<script setup lang="ts">
import { computed, ref } from 'vue'
import type { ICardBasicProps } from '../ts/types'
import {
    EButtonAlignment,
    ECardPhotoSizeDesktop,
    ECardSizeMobile,
    ECardsListLayout,
    ESeoHeadingSize,
} from '../ts/types'

import { useImage } from '../../../composables/useImageUrl'
import { getLinkTarget } from '../../../utils/linkTarget'
import { VButtonVariants } from '@/ui/plushComponents/VButton/types'
import VCarouselSlide from '@/ui/plushComponents/VCarousel/VCarouselSlide.vue'
import { isDark } from '@/ui/helpers/utils'
import { NuxtLink } from '#components'
import { useDrawer } from '@/cms-ui/composables/useDrawer'
import { useClickTracking } from '@/cms-ui/composables/useAnalytics'
import { EClickElement } from '@/cms-ui/utils/dataLayer'

const props = withDefaults(defineProps<ICardBasicProps>(), {
    seoHeadingSize: ESeoHeadingSize.H2,
})

const cardHovered = ref(false)
const cardActive = ref(false)
const buttonRef = ref()
const linkRef = ref()

const { image: responsiveImage } = useImage(props.image, props.page)

const mobileCardSize =
    props.mobileLayout === ECardsListLayout.Grid
        ? {
              [ECardSizeMobile.Small]:
                  'max-md:!w-[calc(50%-0.25rem)] max-md:!max-w-full md:max-lg:!w-[calc(25%-0.75rem)] md:max-lg:!max-w-full',
              [ECardSizeMobile.Large]:
                  'max-md:!w-full max-md:!max-w-full md:max-lg:!w-[calc(50%-0.5rem)] md:max-lg:!max-w-full',
          }
        : {}

const desktopCardSize =
    props.desktopLayout === ECardsListLayout.Grid
        ? {
              [ECardPhotoSizeDesktop.Small]: 'lg:!w-[calc(25%-1.125rem)]',
              [ECardPhotoSizeDesktop.Large]: 'lg:!w-[calc(33.3%-1rem)]',
              [ECardPhotoSizeDesktop.XLarge]: 'lg:!w-[calc(50%-0.75rem)]',
              [ECardPhotoSizeDesktop.XXLarge]: 'lg:!w-full',
              [ECardPhotoSizeDesktop.Fluid]: '',
          }
        : {}

const action = computed(() => {
    if (props.button) {
        return props.button.action
    }
    if (props.link) {
        return props.link.action
    }
    return null
})

const hoverClasses = {
    [VButtonVariants.PRIMARY]: '!text-brand-accent',
    [VButtonVariants.SECONDARY]: '!bg-comp-ghost-hover',
    [VButtonVariants.ACCENT]: '!bg-comp-accent-hover',
    [VButtonVariants.PLAIN]: '!bg-comp-ghost-hover',
}
const activeClasses = {
    [VButtonVariants.PRIMARY]: '!bg-comp-invert-pressed !text-brand-accent',
    [VButtonVariants.SECONDARY]: '!bg-comp-ghost-pressed',
    [VButtonVariants.ACCENT]: '!bg-comp-accent-pressed',
    [VButtonVariants.PLAIN]: '!bg-comp-ghost-pressed',
}

const component = computed(() => {
    if (props.action?.url) {
        return 'a'
    } else if (props.action) {
        return 'button'
    } else {
        return 'div'
    }
})

function handleClick(): void {
    buttonRef?.value?.$el?.click()
    linkRef?.value?.$el?.click()
}
function inactive(): void {
    cardActive.value = false
    cardHovered.value = false
}
function active(): void {
    cardActive.value = true
}
function hover(): void {
    cardHovered.value = true
}

const { openDrawer } = useDrawer()
</script>

<template>
    <VCarouselSlide
        class="group"
        :class="[
            mobileCardSize[mobileSize],
            desktopCardSize[desktopSize],
            { 'max-lg:!px-0 max-md:!basis-[unset] md:max-lg:!basis-[unset]': mobileLayout === ECardsListLayout.Grid },
            { 'lg:!px-0': desktopLayout === ECardsListLayout.Grid },
            {
                'lg:!basis-[unset]':
                    desktopLayout === ECardsListLayout.Grid && desktopSize !== ECardPhotoSizeDesktop.Fluid,
            },
            { 'lg:min-w-0 lg:!flex-1 lg:!px-0': desktopSize === ECardPhotoSizeDesktop.Fluid },
        ]"
    >
        <BackgroundBlock
            :is="component"
            :href="action?.url"
            :data-test="dataTest"
            :target="action ? getLinkTarget(action) : undefined"
            class="rounded-container-lg relative flex size-full flex-col overflow-hidden"
            :class="[{ 'cursor-pointer': button || link }, isDark((dark || cardDark) && !!background)]"
            :page="page"
            :background="background"
            @mouseenter="hover"
            @mouseleave="inactive"
            @mousedown="active"
            @mouseup="inactive"
            @click.stop="handleClick"
        >
            <div>
                <div
                    class="overflow-hidden"
                    :class="{ 'rounded-b-container-lg': !background }"
                >
                    <BaseImage
                        class="duration-400 w-full"
                        :class="{
                            'transition-transform ease-in group-hover:scale-105 group-hover:ease-in-out': action,
                        }"
                        :image-data="responsiveImage"
                        :alt="image?.altText"
                    />
                </div>
            </div>
            <div
                class="flex h-full flex-col justify-between"
                :class="background ? 'p-base pb-md' : 'pt-base pb-md'"
            >
                <div>
                    <component
                        :is="seoHeadingSize"
                        v-if="title"
                        class="heading-xs text-comp-high-contrast line-clamp-3"
                    >
                        {{ title }}
                    </component>
                    <div
                        v-if="paragraph"
                        v-html-gpp="paragraph"
                        class="paragraph-wrapper paragraph-md text-neutral-primary mt-xs pointer-events-none line-clamp-[30]"
                    />
                </div>
                <div class="flex flex-col">
                    <BaseButton
                        v-if="button"
                        :id="`cta-${dataTest}`"
                        ref="buttonRef"
                        class="mt-md max-w-full lg:self-start"
                        :class="[
                            {
                                [hoverClasses[button?.visualVariant]]: cardHovered && !cardActive,
                                [activeClasses[button?.visualVariant]]: cardHovered && cardActive,
                                'max-lg:hidden': mobileSize === ECardSizeMobile.Small,
                                'hidden': !background,
                            },
                            buttonAlignment === EButtonAlignment.LEFT ? 'self-start' : 'self-end',
                        ]"
                        :data-test="`card-basic-${dataTest}`"
                        :variant="button?.visualVariant"
                        size="MEDIUM"
                        :trailing-icon="button?.icon"
                        :dark="(dark || cardDark) && !!background"
                        :href="button.action?.url"
                        :target="getLinkTarget(button?.action)"
                        @click.stop="[
                            useClickTracking({
                                label: button?.label || '',
                                clickPath: button?.action?.url || '',
                                clickElement: EClickElement.BUTTON,
                                componentName: title || 'BasicCard',
                                componentCategory: 'BasicCard',
                            }),
                            openDrawer(button?.action?.drawer, page),
                        ]"
                    >
                        {{ button?.label }}
                    </BaseButton>
                    <VLink
                        v-if="link"
                        :id="`cta-${dataTest}`"
                        ref="linkRef"
                        :to="action?.url"
                        class="text-comp-high-contrast mt-base max-w-full self-start"
                        :dark="(dark || cardDark) && !!background"
                        size="MEDIUM"
                        :data-test="`card-basic-${dataTest}`"
                        :download="link.action?.downloadedAsset?.asset?.links?.site?.href"
                        :target="getLinkTarget(link?.action)"
                        @click.stop="[
                            useClickTracking({
                                label: link?.label || '',
                                clickPath: link.action?.downloadedAsset?.asset?.links?.site?.href || '',
                                clickElement: EClickElement.BUTTON,
                                componentName: title || 'BasicCard',
                                componentCategory: 'BasicCard',
                            }),
                            openDrawer(link?.action?.drawer, page),
                        ]"
                    >
                        {{ link.label }}

                        <VIcon
                            v-if="link.visualVariant === 'chevron'"
                            name="chevron-right"
                            size="SMALL"
                            data-test="CARD-BASIC_LINK-CHEVRON"
                        />
                    </VLink>
                </div>
            </div>
        </BackgroundBlock>
    </VCarouselSlide>
</template>

<style scoped>
.paragraph-wrapper > p {
  @apply line-clamp-5;
}
</style>
